/**
 * PhotonRemote UDP packet reassembly — Rust tarafı packetizer ile birebir.
 *
 * Wire header: 11 bytes
 *   byte 0: VV TTT FFF (version 2b | packet_type 3b | flags 3b)
 *   bytes 1-3: session_id (24b BE)
 *   bytes 4-7: sequence (32b BE)
 *   byte 8: channel
 *   bytes 9-10: payload_length (16b BE)
 *
 * Video subheader: 4 bytes
 *   byte 0: keyframe bit (0x80)
 *   bytes 1-3: pts_90khz (24b BE)
 */
// NOTE: Channel + PacketType export'ları packet-header.ts'e taşındı.
// Geriye uyumluluk için alias'la re-export.
export { Channel, PacketType } from './packet-header';
import { Channel as _Channel, PacketType as _PacketType } from './packet-header';
export const VideoFlags = {
    FragStart: 0b001,
    FragEnd: 0b010,
    Keyframe: 0b100,
};
const HEADER_SIZE = 11;
const VIDEO_SUBHEADER_SIZE = 4;
export function decodeHeader(bytes) {
    if (bytes.length < HEADER_SIZE)
        return null;
    const b0 = bytes[0];
    const version = (b0 >> 6) & 0b11;
    const packetType = ((b0 >> 3) & 0b111);
    const flags = b0 & 0b111;
    if (packetType > _PacketType.Control)
        return null;
    const sessionId = (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
    const sequence = ((bytes[4] << 24) >>> 0) | (bytes[5] << 16) |
        (bytes[6] << 8) | bytes[7];
    const channel = bytes[8];
    if (channel > _Channel.Files)
        return null;
    const payloadLength = (bytes[9] << 8) | bytes[10];
    return {
        version, packetType, flags, sessionId, sequence, channel, payloadLength,
    };
}
export function decodeVideoSubheader(bytes) {
    if (bytes.length < VIDEO_SUBHEADER_SIZE)
        return null;
    const isKeyframe = (bytes[0] & 0x80) !== 0;
    const pts = (bytes[1] << 16) | (bytes[2] << 8) | bytes[3];
    return { isKeyframe, pts90khz: pts };
}
/**
 * H.264 frame reassembler. Controller tarafı UdpVideoReceiver ile aynı FSM.
 * Sequence gap → mevcut frame drop, FRAG_START bekler.
 */
export class VideoReassembler {
    onFrame;
    buffer = [];
    expectedSequence = 0;
    hasReassembly = false;
    droppedGaps = 0;
    framesAssembled = 0;
    constructor(onFrame) {
        this.onFrame = onFrame;
    }
    process(wire) {
        const hdr = decodeHeader(wire);
        if (!hdr)
            return;
        if (hdr.channel !== _Channel.Video || hdr.packetType !== _PacketType.Data)
            return;
        if (wire.length < HEADER_SIZE + VIDEO_SUBHEADER_SIZE)
            return;
        const sub = decodeVideoSubheader(wire.subarray(HEADER_SIZE, HEADER_SIZE + VIDEO_SUBHEADER_SIZE));
        if (!sub)
            return;
        const payload = wire.subarray(HEADER_SIZE + VIDEO_SUBHEADER_SIZE);
        const isFragStart = (hdr.flags & VideoFlags.FragStart) !== 0;
        const isFragEnd = (hdr.flags & VideoFlags.FragEnd) !== 0;
        if (this.hasReassembly && hdr.sequence !== this.expectedSequence) {
            this.hasReassembly = false;
            this.buffer = [];
            this.droppedGaps++;
            if (!isFragStart)
                return;
        }
        if (isFragStart) {
            this.buffer = [];
            this.hasReassembly = true;
        }
        if (!this.hasReassembly)
            return;
        this.buffer.push(new Uint8Array(payload));
        this.expectedSequence = (hdr.sequence + 1) >>> 0;
        if (isFragEnd) {
            const total = this.buffer.reduce((a, b) => a + b.length, 0);
            const out = new Uint8Array(total);
            let off = 0;
            for (const p of this.buffer) {
                out.set(p, off);
                off += p.length;
            }
            this.buffer = [];
            this.hasReassembly = false;
            this.framesAssembled++;
            this.onFrame(out, sub.pts90khz, sub.isKeyframe);
        }
    }
}
